import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { FilterField, FilterOperator, Ticket, TicketUser } from 'src/app/models';
import {
  CompanyService,
  CustomerService,
  TcTicketService,
  TcTicketTypeService,
  TicketStatusService,
  UserService,
} from 'src/app/shared/services';
import { ToastService } from '@teorema/patron';
import { constants } from '@app/shared/utils/constants';

@Component({
  selector: 'app-new-ticket',
  templateUrl: './new-ticket.component.html',
  standalone: false,
})
export class NewTicketComponent implements OnInit {
  formGroup!: FormGroup;
  loadingSave: boolean = false;
  ticket: Ticket = new Ticket();
  types = constants.ticketcenter.ticketsTypes;
  ticketUsers: TicketUser[] = [];
  idTemp: number = 0;

  constructor(
    private router: Router,
    private formBuilder: FormBuilder,
    private ticketService: TcTicketService,
    private toastService: ToastService,
    private companyService: CompanyService,
    private customerService: CustomerService,
    private ticketStatusService: TicketStatusService,
    private userService: UserService,
    private ticketTypeService: TcTicketTypeService
  ) {
    this.formGroup = this.formBuilder.group({
      company: [null, [Validators.required]],
      customer: ['', [Validators.required]],
      system: [null, [Validators.required]],
      phase: [''],
      summary: [''],
      problem: ['', [Validators.required]],
      userResp: ['', [Validators.required]],
      userDes: [''],
      ticketType: ['', [Validators.required]],
      issue: [''],
      project: [''],
      sprint: [''],
      type: [''],
      status: [''],
      requester: [''],
      phone: [''],
      email: [''],
      priority: [9],
      estimatedEffort: [''],
      commercialEstimatedEffort: [''],
      tags: [''],
    });
  }

  ngOnInit() {
    this.idTemp = new Date().getTime();
    this.setDefaultValues();
  }

  async setDefaultValues() {
    this.formGroup.get('type')?.patchValue(this.types.find(t => t.value === 'P'));

    let response = await this.companyService
      .findAll({
        page: 1,
        perPage: 1,
        filters: [
          new FilterField({
            name: 'equal[companies.ref]',
            value: '0001',
            operator: FilterOperator.has,
            label: '',
          }),
        ],
      })
      .catch(e => {
        console.error(e);
        this.toastService.show({ color: 'error', message: 'Houve um erro ao carregar os dados' });
      });

    if (response?.data?.length) {
      response!.data[0]._name = response!.data[0]['ref_name'];
      response!.data[0].id = response!.data[0].person_id!;
      this.formGroup.get('company')?.patchValue(response!.data[0]);
    }

    response = await this.customerService
      .findAll({
        page: 1,
        perPage: 1,
        filters: [
          new FilterField({
            name: 'equal[customers.ref]',
            value: '0001_0000001',
            operator: FilterOperator.has,
            label: '',
          }),
        ],
      })
      .catch(e => {
        console.error(e);
        this.toastService.show({ color: 'error', message: 'Houve um erro ao carregar os dados' });
      });

    if (response?.data?.length) {
      response!.data[0]._name = response!.data[0]['ref_name'];
      this.formGroup.get('customer')?.patchValue(response!.data[0]);
    }

    response = await this.ticketStatusService
      .findAll({
        page: 1,
        perPage: 1,
        filters: [
          new FilterField({
            name: 'equal[description]',
            value: 'Analise Detalhada',
            operator: FilterOperator.has,
            label: '',
          }),
        ],
      })
      .catch(e => {
        console.error(e);
        this.toastService.show({ color: 'error', message: 'Houve um erro ao carregar os dados' });
      });

    if (response?.data?.length) {
      this.formGroup.get('status')?.patchValue(response!.data[0]);
    }

    response = await this.ticketTypeService
      .findAll({
        page: 1,
        perPage: 1,
        filters: [
          new FilterField({
            name: 'equal[description]',
            value: 'Chamada Padrao',
            operator: FilterOperator.has,
            label: '',
          }),
        ],
      })
      .catch(e => {
        console.error(e);
        this.toastService.show({ color: 'error', message: 'Houve um erro ao carregar os dados' });
      });

    if (response?.data?.length) {
      this.formGroup.get('ticketType')?.patchValue(response!.data[0]);
    }

    let responsibles: any = localStorage.getItem('responsibles');

    if (responsibles) {
      try {
        responsibles = JSON.parse(responsibles);

        if (responsibles?.length) {
          responsibles[0]._name = responsibles[0]['name'];
          this.formGroup.get('userResp')?.patchValue(responsibles[0]);
        }
      } catch (e) {
        console.error(e);
        this.toastService.show({ color: 'error', message: 'Houve um erro ao carregar os dados' });
      }
    }

    let currentUser = await this.userService.getCurrentUser().catch(e => {
      console.error(e);
      this.toastService.show({
        color: 'error',
        message: 'Houve um erro ao carregar os dados',
      });
    });

    if (currentUser?.id) {
      currentUser._name = currentUser['name'];
      this.formGroup.get('userDes')?.patchValue(currentUser);
    }
  }

  async save() {
    if (this.loadingSave) return;

    if (this.formGroup.valid) {
      let system = this.formGroup.get('system')!.value;
      let company = this.formGroup.get('company')!.value;
      let customer = this.formGroup.get('customer')!.value;
      let phase = this.formGroup.get('phase')!.value;
      let userResp = this.formGroup.get('userResp')!.value;
      let userDes = this.formGroup.get('userDes')!.value;
      let ticketType = this.formGroup.get('ticketType')!.value;
      let issue = this.formGroup.get('issue')!.value;
      let sprint = this.formGroup.get('sprint')!.value;
      let project = this.formGroup.get('project')!.value;
      let status = this.formGroup.get('status')!.value;
      let phone = this.formGroup.get('phone')!.value;

      if (phone) {
        phone = `55${phone.replace(/\D/g, '')}`;
      }

      let ticket = {
        company: company,
        company_id: company.id,
        customer: customer,
        customer_id: customer.id,
        system: system,
        system_id: system.id,
        phase: phase,
        phase_id: phase.id,
        ticket_type: ticketType,
        ticket_type_id: ticketType.id,
        issue: issue,
        issue_id: issue.id,
        sprint: sprint,
        sprint_id: sprint.id,
        project: project,
        project_id: project.id,
        summary: this.formGroup.get('summary')!.value,
        problem: this.formGroup.get('problem')!.value,
        ticket_status: status,
        ticket_status_id: status.id,
        type: this.formGroup.get('type')!.value.value,
        requester: this.formGroup.get('requester')!.value,
        phone: phone,
        email: this.formGroup.get('email')!.value,
        priority: this.formGroup.get('priority')!.value,
        estimated_effort: this.formGroup.get('estimatedEffort')!.value,
        commercial_estimated_effort: this.formGroup.get('commercialEstimatedEffort')!.value,
        ticket_tags: this.formGroup.get('tags')!.value,
        documents: this.ticket.documents || [],
        responsible: userResp,
        developer: userDes,
      };

      this.loadingSave = true;

      let response = await this.ticketService
        .save(ticket as unknown as Ticket)
        .catch(e => {
          this.toastService.showResponseError(e, 'Houve um erro ao salvar os dados do formulário!');
        })
        .finally(() => {
          this.loadingSave = false;
        });

      if (response?.id) {
        this.toastService.show({
          color: 'success',
          message: 'Chamado salvo com sucesso!',
        });
        this.router.navigate(['/chamados/editar/' + response.id]);
      }
    } else {
      this.toastService.show({
        color: 'error',
        message: 'Formulário inválido',
      });
    }
  }
}
